import type { RunDetail, RunRow } from '../lib/payruns';
import { formatDate, formatHours, formatRs } from '../lib/money';
import { DocHeader, Pages, Stamp } from '../components/PrintPreview';
import StaffStamp from '../components/StaffStamp';
import SignatureImage from '../components/SignatureImage';
import { handedRs, payrunApi, shortRs } from '../lib/payruns';

/**
 * The pay run pack, A4.
 *
 * "Page 1 the summary — numbered workers, days, OT, gross, advances, EPF, net,
 * totals, cash needed, an EPF/ETF block and a blank signature column — then one
 * payslip per worker, one per A4."
 *
 * The whole pack prints unapproved with the stamp, because that is what the
 * owner reads before he approves. Standalone payslips print only afterwards —
 * see `Payslips` below.
 */

interface Props {
  detail: RunDetail;
  printedBy: string;
  printedAt: Date;
}

export default function PayRunPack({ detail, printedBy, printedAt }: Props) {
  const approved = detail.run.status === 'approved';
  const pages = [
    <Summary key="summary" detail={detail} printedBy={printedBy} printedAt={printedAt} />,
    ...detail.rows.map((row) => (
      <Payslip key={row.id} detail={detail} row={row} approved={approved} />
    )),
  ];
  return <Pages pages={pages} />;
}

/** The pack without page 1 — only once the owner has approved. */
export function Payslips({ detail }: { detail: RunDetail }) {
  return (
    <Pages
      pages={detail.rows.map((row) => (
        <Payslip key={row.id} detail={detail} row={row} approved />
      ))}
    />
  );
}

// ---------------------------------------------------------------- page 1

function Summary({ detail, printedBy, printedAt }: Props) {
  const { run, rows } = detail;
  const approved = run.status === 'approved';
  const hasContributions = run.epf_employee_rs + run.epf_employer_rs + run.etf_rs > 0;
  // A Loan column only when the run took something off a loan — a run from
  // before loans existed prints exactly as it always did.
  const hasLoans = run.loan_rs > 0;
  // Likewise a column for a shortfall brought from the last run, only when there is one.
  const carriedIn = rows.reduce((sum, row) => sum + (row.carried_in_rs ?? 0), 0);
  const carriedOut = rows.reduce((sum, row) => sum + shortRs(row.take_home_rs), 0);

  return (
    <>
      <DocHeader
        doc={run.cycle === 'monthly' ? 'Monthly pay run — EPF/ETF members' : 'Pay run — summary'}
        title={
          `${formatDate(run.period_from)} to ${formatDate(run.period_to)}` +
          (run.pay_on ? ` · pay day ${formatDate(run.pay_on)}` : '')
        }
        sub={`Prepared by ${run.created_by_name} · printed ${printedAt.toLocaleString('en-GB')} by ${printedBy}`}
      />

      <Stamp
        approved={approved}
        when={run.approved_at}
        label="check and sign before the money goes out"
      />

      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Employee</th>
            <th className="num">Days</th>
            <th className="num">OT h</th>
            <th className="num">Gross</th>
            <th className="num">Advance</th>
            <th className="num">EPF {run.epf_employee_pct_x100 / 100}%</th>
            {carriedIn > 0 && <th className="num">Short from last pay</th>}
            {hasLoans && <th className="num">Loan</th>}
            <th className="num">Net pay</th>
            {/* The column he signs in when the cash is handed over. */}
            <th style={{ width: 90 }}>Signature</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id}>
              <td>{index + 1}</td>
              <td>
                {row.worker_name}
                {row.is_epf_member === 1 && row.epf_employee_rs > 0 && (
                  <div style={{ fontSize: 10 }}>EPF {row.epf_no ?? 'member'}</div>
                )}
              </td>
              <td className="num">{row.days_worked}</td>
              <td className="num">{formatHours(row.ot_tenths)}</td>
              <td className="num">{formatRs(row.gross_rs)}</td>
              <td className="num">{row.advances_rs ? `−${formatRs(row.advances_rs)}` : '—'}</td>
              <td className="num">
                {row.epf_employee_rs ? `−${formatRs(row.epf_employee_rs)}` : '—'}
              </td>
              {carriedIn > 0 && (
                <td className="num">{row.carried_in_rs ? `−${formatRs(row.carried_in_rs)}` : '—'}</td>
              )}
              {hasLoans && (
                <td className="num">{row.loan_rs ? `−${formatRs(row.loan_rs)}` : '—'}</td>
              )}
              <td className="num">
                {formatRs(handedRs(row.take_home_rs))}
                {shortRs(row.take_home_rs) > 0 && (
                  <div style={{ fontSize: 10 }}>short {formatRs(shortRs(row.take_home_rs))} → next pay</div>
                )}
              </td>
              {/* His box: the signature he gave on the tablet for his pay. */}
              <td className="sigcell">
                {row.signed ? (
                  <SignatureImage path={payrunApi.payslipSignaturePath(run.id, row.worker_id, row.signed_at)} />
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={4}>Totals — {rows.length} employees</th>
            <th className="num">{formatRs(run.gross_rs)}</th>
            <th className="num">−{formatRs(run.advances_rs)}</th>
            <th className="num">−{formatRs(run.epf_employee_rs)}</th>
            {carriedIn > 0 && <th className="num">−{formatRs(carriedIn)}</th>}
            {hasLoans && <th className="num">−{formatRs(run.loan_rs)}</th>}
            <th className="num">{formatRs(run.net_rs)}</th>
            <th />
          </tr>
        </tfoot>
      </table>

      <div style={{ marginTop: 10, fontSize: 12 }}>
        Cash needed to pay the employees: <b>{formatRs(run.net_rs)}</b>. Advances shown above
        were already paid out in cash during the week against signed receipts.
        {hasLoans && <> Loan repayments of {formatRs(run.loan_rs)} are kept back from pay.</>}
        {carriedOut > 0 && (
          <> Where advances were more than the pay, {formatRs(carriedOut)} in all is carried to the
          next pay.</>
        )}
      </div>

      {hasContributions && (
        <>
          <h4 style={{ marginTop: 12 }}>EPF and ETF for this run</h4>
          <table>
            <tbody>
              <tr>
                <td>Base salary earned by members</td>
                <td className="num">{formatRs(run.base_earned_rs)}</td>
              </tr>
              <tr>
                <td>
                  EPF employee {run.epf_employee_pct_x100 / 100}% — withheld from pay above
                </td>
                <td className="num">{formatRs(run.epf_employee_rs)}</td>
              </tr>
              <tr>
                <td>EPF employer {run.epf_employer_pct_x100 / 100}%</td>
                <td className="num">{formatRs(run.epf_employer_rs)}</td>
              </tr>
              <tr>
                <td>ETF employer {run.etf_pct_x100 / 100}%</td>
                <td className="num">{formatRs(run.etf_rs)}</td>
              </tr>
              <tr>
                <th>Total payable to EPF and ETF</th>
                <th className="num">
                  {formatRs(run.epf_employee_rs + run.epf_employer_rs + run.etf_rs)}
                </th>
              </tr>
            </tbody>
          </table>
        </>
      )}

      <div className="siglines">
        <StaffStamp
          label="Prepared by (office)"
          userId={run.created_by}
          name={run.created_by_name}
          at={run.created_at}
        />
        <StaffStamp
          label="Approved by (owner)"
          userId={run.approved_by}
          name={run.approved_by_name}
          at={run.approved_at}
        />
      </div>

      <div style={{ marginTop: 14, fontSize: 11 }}>
        Payslips for each employee follow, one per page.
      </div>
    </>
  );
}

// ------------------------------------------------------- one payslip

function Payslip({
  detail, row, approved,
}: {
  detail: RunDetail;
  row: RunRow;
  approved: boolean;
}) {
  const { run } = detail;
  const days = detail.days
    .filter((day) => day.worker_id === row.worker_id)
    .sort((a, b) => a.work_date.localeCompare(b.work_date));
  const advances = detail.advances
    .filter((advance) => advance.worker_id === row.worker_id)
    .sort((a, b) => a.work_date.localeCompare(b.work_date));
  const loans = (detail.loans ?? []).filter((loan) => loan.worker_id === row.worker_id);

  // "EPF and ETF detail is hidden entirely for non-members and for members with
  // no contribution in the period. Never print rows of zeros."
  const showEpf = row.is_epf_member === 1 && row.base_earned_rs > 0;

  return (
    <>
      <div className="payslip-head">
        <div>
          <div className="docname">Payslip</div>
          <div className="doctitle">
            {formatDate(run.period_from)} to {formatDate(run.period_to)}
            {run.pay_on && <> · paid {formatDate(run.pay_on)}</>}
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: 13 }}>
          <b>{row.worker_name}</b>
          <br />
          {row.roles_label}
        </div>
      </div>

      {!approved && <Stamp approved={false} label="not for payment until approved" />}

      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Job done</th>
            <th className="num">Hrs</th>
            <th className="num">OT</th>
            <th className="num">Salary</th>
            <th className="num">OT pay</th>
            <th className="num">Allowance</th>
            <th className="num">Earned</th>
          </tr>
        </thead>
        <tbody>
          {days.map((day) => (
            <tr key={day.work_date}>
              <td>{formatDate(day.work_date)}</td>
              <td>
                {day.role_name ?? '—'}
                {day.pay_basis === 'trip' && ' (allowance only)'}
                {/* The reason for the allowance, on the payslip he takes home. */}
                {day.allowance_reason && (
                  <>
                    <br />
                    <span style={{ fontSize: 10 }}>{day.allowance_reason}</span>
                  </>
                )}
              </td>
              <td className="num">{day.pay_basis === 'trip' ? '—' : formatHours(day.hours_tenths)}</td>
              <td className="num">{day.ot_tenths ? formatHours(day.ot_tenths) : '—'}</td>
              <td className="num">{day.salary_rs ? formatRs(day.salary_rs) : '—'}</td>
              <td className="num">{day.ot_rs ? formatRs(day.ot_rs) : '—'}</td>
              <td className="num">
                {day.allowance_rs + day.sunday_allowance_rs
                  ? formatRs(day.allowance_rs + day.sunday_allowance_rs)
                  : '—'}
              </td>
              <td className="num">{formatRs(day.gross_rs)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={7}>
              Gross earnings — {row.days_worked} days worked
              {row.ot_tenths > 0 && `, ${formatHours(row.ot_tenths)} OT hours`}
            </th>
            <th className="num">{formatRs(row.gross_rs)}</th>
          </tr>
        </tfoot>
      </table>

      {showEpf && (
        <table style={{ marginTop: 8 }}>
          <tbody>
            <tr>
              <td>Base salary earned</td>
              <td className="num">{formatRs(row.base_earned_rs)}</td>
            </tr>
            {/* A member's EPF/ETF is on his EPF salary, once per pay period
                (17 October 2026). The stored basis is what this run used. */}
            {row.epf_salary_rs !== null && (
              <tr>
                <td>
                  EPF and ETF are worked out on the EPF salary — {formatRs(row.epf_salary_rs)} a{' '}
                  {run.cycle === 'monthly' ? 'month' : 'week'}
                </td>
                <td className="num">{formatRs(row.epf_basis_rs)}</td>
              </tr>
            )}
            <tr>
              <td>
                EPF employee share {run.epf_employee_pct_x100 / 100}% — deducted from this pay
              </td>
              <td className="num">−{formatRs(row.epf_employee_rs)}</td>
            </tr>
            <tr>
              <td>
                EPF employer share {run.epf_employer_pct_x100 / 100}% — paid by the quarry
                {row.epf_no && ` · EPF no ${row.epf_no}`}
              </td>
              <td className="num">{formatRs(row.epf_employer_rs)}</td>
            </tr>
            <tr>
              <td>ETF {run.etf_pct_x100 / 100}% — paid by the quarry</td>
              <td className="num">{formatRs(row.etf_rs)}</td>
            </tr>
            <tr>
              <th>Credited to his fund</th>
              <th className="num">
                {formatRs(row.epf_employee_rs + row.epf_employer_rs + row.etf_rs)}
              </th>
            </tr>
          </tbody>
        </table>
      )}

      {advances.length > 0 && (
        <table style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>Advances already taken in cash</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {advances.map((advance, index) => (
              <tr key={`${advance.work_date}-${index}`}>
                <td>
                  {formatDate(advance.work_date)}
                  {advance.note && ` — ${advance.note}`}
                </td>
                <td className="num">−{formatRs(advance.amount_rs)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>Total advances</th>
              <th className="num">−{formatRs(row.advances_rs)}</th>
            </tr>
          </tfoot>
        </table>
      )}

      {/* What this run took off his loans, and what he still owes after it —
          as it was when the run was made. */}
      {loans.length > 0 && (
        <table style={{ marginTop: 8 }}>
          <thead>
            <tr>
              <th>Loan repayment</th>
              <th className="num">Still owed after</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {loans.map((loan) => (
              <tr key={loan.loan_id}>
                <td>
                  Loan of {formatRs(loan.loan_amount_rs)} given {formatDate(loan.given_on)}
                  {loan.reason && ` — ${loan.reason}`}
                </td>
                <td className="num">{formatRs(loan.balance_after_rs)}</td>
                <td className="num">−{formatRs(loan.amount_rs)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {row.carried_in_rs > 0 && (
        <table style={{ marginTop: 8 }}>
          <tbody>
            <tr>
              <td>Short from last pay — advances were more than that pay</td>
              <td className="num">−{formatRs(row.carried_in_rs)}</td>
            </tr>
          </tbody>
        </table>
      )}

      {/* Boxed: the figure he is handed. */}
      <div className="netbox">
        <span>Net pay</span>
        <span>{formatRs(handedRs(row.take_home_rs))}</span>
      </div>
      {shortRs(row.take_home_rs) > 0 && (
        <div style={{ marginTop: 6, fontSize: 12 }}>
          Advances were more than this pay. Short {formatRs(shortRs(row.take_home_rs))} — carried to
          his next pay.
        </div>
      )}

      {/* He signs for his pay on the tablet; until he has, the paper line. */}
      {row.signed ? (
        <div style={{ marginTop: 16, fontSize: 12 }}>
          Received by (employee):
          <div className="sigprint" style={{ marginTop: 4 }}>
            <SignatureImage path={payrunApi.payslipSignaturePath(run.id, row.worker_id, row.signed_at)} />
          </div>
          <div style={{ fontSize: 11 }}>
            Signed on the tablet, {row.signed_at ? new Date(row.signed_at).toLocaleString('en-GB') : ''}
          </div>
        </div>
      ) : (
        <div style={{ marginTop: 26, fontSize: 12 }}>
          Received by: ______________________     Date: ____________
        </div>
      )}
    </>
  );
}
